import { computed, inject, Injectable, signal } from '@angular/core';
import {
  AGENCY_TYPE,
  AgencyKeys,
  Agent,
  AGENT_STATUS,
  AGENT_TYPE,
  AgentKeys,
  ApproveDenyReasonVisibilityLevel,
  ChangeSourceType,
} from '@ag-common-lib/public-api';
import {
  AgencyService,
  AGENTS_FIRESTORE_COLLECTION,
  AgentService,
  BaseFormService,
  ChangeSourceTypeService,
} from 'ag-common-svc/public-api';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import CustomStore from 'devextreme/data/custom_store';
import { isEqual, pick } from 'lodash';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { DxFilterOperators } from '@ag-common-lib/lib';
import { getAgentFullName } from 'ag-common-svc/lib/utils/agent-data.util';
import { ModalWindowUserAction } from '../modal-window/modal-window.model';
import {
  BatchCommand,
  BatchCommandKeys,
  BatchCommandType,
  BatchOperationsService,
} from 'ag-common-svc/lib/dao/batch-firestore.service';
import { AgentsTransferModalService } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agents-transfer-modal.service';
import { BehaviorSubject } from 'rxjs';
import { AgencyHierarchyManagerMode } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.model';
import { PortalDataService } from 'ag-common-svc/lib/services/portal.service';
import { ToastrService } from 'ngx-toastr';

export interface ChangeAgentStatusConfig {
  reasonRequired: boolean;
  title: string;
  actionTitle: string;
  agentStatus: AGENT_STATUS;
  approveDenyReasonVisibilityLevel: ApproveDenyReasonVisibilityLevel;
}

@Injectable()
export class AgentDispositionModalService extends BaseFormService<Agent> {
  private portalDataService = inject(PortalDataService);

  private readonly _isAgentIdGenerationInProgress$ = new BehaviorSubject<boolean>(false);
  isAgentIdGenerationInProgress$ = this._isAgentIdGenerationInProgress$.asObservable();

  private readonly _isTransferInProgress$ = new BehaviorSubject<boolean>(false);
  isTransferInProgress$ = this._isTransferInProgress$.asObservable();

  bypassValidationSig = signal(false);

  protected agenciesSig = toSignal(this.agencyService.agencies$);

  mgasSig = toSignal(this.agencyService.mgas$);

  private _mgaIdSig = computed(() => {
    const mgaId = this.formDataSig()?.[AgentKeys.p_mga_id];

    return mgaId;
  });

  private _agentIdIdSig = computed(() => {
    const agentId = this.formDataSig()?.[AgentKeys.p_agent_id];

    return agentId;
  });

  mgaAgenciesSig = computed(() => {
    const agencies = this.agenciesSig();
    const mgaId = this._mgaIdSig();

    return agencies?.filter(a => a.mga == mgaId || a.agency_id == mgaId);
  });

  mgaLeadAgentIdSig = computed(() => {
    const agencies = this.agenciesSig();
    const mgaId = this._mgaIdSig();
    const mga = agencies?.find(a => a.agency_id == mgaId);
    const mgaLeadAgentId = mga?.[AgencyKeys.agencyLead];

    console.log('mgaLeadAgentId', mgaLeadAgentId);

    return mgaLeadAgentId;
  });

  isManagerFieldVisibleSig = computed(() => {
    const mgaLeadAgentId = this.mgaLeadAgentIdSig();
    const agentId = this._agentIdIdSig();

    if (!mgaLeadAgentId) {
      return true;
    }

    return mgaLeadAgentId !== agentId;
  });

  agenciesLeadsMapSig = computed(() => {
    const mgaAgencies = this.mgaAgenciesSig();
    const map = new Map();

    mgaAgencies?.forEach(agency => {
      const leadId = agency?.[AgencyKeys.agencyLead];
      if (!leadId) {
        return;
      }
      map.set(leadId, agency);
    });

    return map;
  });

  commonAgentStatsSig = computed(
    () => {
      const agent = this.formDataSig();
      const mgaId = agent?.[AgentKeys.p_mga_id];
      const agencyId = agent?.[AgentKeys.p_agency_id];
      const agentId = agent?.[AgentKeys.p_agent_id];
      const agenciesLeadsMap = this.agenciesLeadsMapSig();

      if (agenciesLeadsMap.has(agentId)) {
        const agency = agenciesLeadsMap.get(agentId);
        const agencyType = agency?.[AgencyKeys.agencyType];

        switch (agencyType) {
          case AGENCY_TYPE.MGA:
            return { agencyId: null, agentId };

          case AGENCY_TYPE.AGENCY:
            return { agencyId: mgaId, agentId };
        }
      }

      return { agencyId, agentId, mgaId };
    },
    { equal: isEqual },
  );

  getAgencyManagersDataSource = (mgaId: string) => {
    const currentAgentDbId = this.formDataSig()?.[BaseModelKeys.dbId];
    return {
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      map: data => {
        return Object.assign({}, data, {
          [AgentKeys.p_agent_name]: getAgentFullName(data),
        });
      },
      store: new CustomStore({
        byKey: payload => {
          return this.agentSearchService.getByAgentId(payload);
        },
        load: async loadOptions => {
          loadOptions.filter = [
            [AgentKeys.is_manager, DxFilterOperators.equal, true],
            DxFilterOperators.and,
            [AgentKeys.p_mga_id, DxFilterOperators.equal, mgaId],
          ];

          const response = await this.agentSearchService.search(loadOptions);
          // Exclude current agent from Team Lead dropdown
          if (Array.isArray(response?.data)) {
            response.data = response.data.filter(
              agent => agent[BaseModelKeys.dbId] !== currentAgentDbId && agent[AgentKeys.p_agent_id],
            );
          }
          return response;
        },
      }),
    };
  };

  constructor(
    private agentService: AgentService,
    private agencyService: AgencyService,
    private agentSearchService: AgentSearchService,
    private agentsTransferModalService: AgentsTransferModalService,
    private batchOperationsService: BatchOperationsService,
    private changeSourceTypeService: ChangeSourceTypeService,
    private toastrService: ToastrService,
  ) {
    super();
  }

  handleSave = async (sourceType: ChangeSourceType) => {
    this.startProgress();
    const formData = this.formDataSig();
    const agentDbId = formData?.[BaseModelKeys.dbId];
    const agentId = formData?.[AgentKeys.p_agent_id];
    const agentFullname = formData?.[AgentKeys.p_agent_name];
    const isTeamLead = formData?.[AgentKeys.is_manager];
    const isManagerStatusChanged = this.formChangesDetector.checkIsFieldChanged(AgentKeys.is_manager);
    const isMgaChanged = this.formChangesDetector.checkIsFieldChanged(AgentKeys.p_mga_id);

    if (!isTeamLead && !isManagerStatusChanged) {
      await this.applyChanges(sourceType);
      this.stopProgress();
      return;
    }

    if (isTeamLead && !isMgaChanged) {
      await this.applyChanges(sourceType);
      this.stopProgress();
      return;
    }

    const team =
      (await this.agentSearchService.getTeamByAgentId(agentId).catch(error => {
        this.stopProgress();
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Circular hierarchy detected. Reassign one of the Team Leads before continuing.';
        const htmlMessage = `
          <p><b>Unable to load team hierarchy.</b><br><br>
          ${message}<br><br>
          <i>Full chain is available in the browser console.</i><br><br>
          Please update the Team Lead assignment and try again.</p>
        `;
        this.toastrService.error(htmlMessage, 'Unable to load team', {
          disableTimeOut: true,
          enableHtml: true,
        });
        throw error;
      })) ?? [];

    if (!team?.length) {
      await this.applyChanges(sourceType);
      this.stopProgress();
      return;
    }

    const workSheetName = `${agentFullname} Team`;
    const originMgaId = isMgaChanged
      ? this.formChangesDetector.getInitialValue(AgentKeys.p_mga_id)
      : formData?.[AgentKeys.p_mga_id];
    const targetMgaId = isMgaChanged ? formData?.[AgentKeys.p_mga_id] : null;
    const results = await this.agentsTransferModalService.transferTeam({
      mode: AgencyHierarchyManagerMode.agentTransfer,
      workSheetName,
      transferedAgent: formData,
      originMgaId,
      targetMgaId,
    });

    if (results?.type !== ModalWindowUserAction.apply) {
      return;
    }

    this._isTransferInProgress$.next(true);

    try {
      const command: Omit<BatchCommand, BatchCommandKeys.dbId | BatchCommandKeys.data> = {
        [BatchCommandKeys.table]: AGENTS_FIRESTORE_COLLECTION,
        [BatchCommandKeys.toFirestore]: AgentService.toFirestore,
        [BatchCommandKeys.type]: BatchCommandType.update,
      };
      const commands: BatchCommand[] = [];
      const hierarchyChanges = results?.data?.hierarchyChanges;
      const targetMgaLeadSelection = results?.data?.targetMgaLeadSelection;

      const targetAgentChanges = this.getChanges();
      if (!hierarchyChanges?.[agentDbId]) {
        commands.push(
          Object.assign({}, command, {
            [BatchCommandKeys.dbId]: agentDbId,
            [BatchCommandKeys.data]: targetAgentChanges,
          }),
        );
      }

      Object.entries(hierarchyChanges).forEach(([changedAgentDbId, hierarchyChange]) => {
        const change = {
          [BaseModelKeys.actionName]: this.changeSourceTypeService.sourceType(),
        };

        if (changedAgentDbId === agentDbId) {
          Object.assign(change, targetAgentChanges);
        }

        Object.assign(
          change,
          pick(hierarchyChange, [
            AgentKeys.manager_id,
            AgentKeys.p_mga_id,
            AgentKeys.p_agency_id,
            AgentKeys.is_manager,
          ]),
        );

        commands.push(
          Object.assign({}, command, {
            [BatchCommandKeys.dbId]: changedAgentDbId,
            [BatchCommandKeys.data]: change,
          }),
        );
      });

      if (targetMgaLeadSelection?.targetMgaDbId && targetMgaLeadSelection?.targetMgaLeadAgentId) {
        commands.push({
          [BatchCommandKeys.table]: 'agencies',
          [BatchCommandKeys.type]: BatchCommandType.update,
          [BatchCommandKeys.dbId]: targetMgaLeadSelection.targetMgaDbId,
          [BatchCommandKeys.data]: {
            [BaseModelKeys.actionName]: this.changeSourceTypeService.sourceType(),
            [AgencyKeys.agencyLead]: targetMgaLeadSelection.targetMgaLeadAgentId,
          },
        });
      }

      await this.batchOperationsService.runBatchOperations(commands);
      this._isTransferInProgress$.next(false);
    } catch (error) {
      this._isTransferInProgress$.next(false);

      throw error;
    }
  };

  private getChanges = (): Partial<Agent> => {
    const formData = this.formDataSig();

    const changes = this.formChangesDetector.getAllChanges();
    const updates = {};

    changes.forEach(([key]) => {
      Object.assign(updates, { [key]: formData[key] });
    });

    return updates;
  };

  private applyChanges = (sourceType: ChangeSourceType) => {
    const formData = this.formDataSig();
    const agentDbId = formData?.[BaseModelKeys.dbId];
    const updates = this.getChanges();

    return this.agentService.updateAgentFields(agentDbId, updates, sourceType);
  };

  setFormData = (agent?: Agent) => {
    this.stopProgress();
    const initialData = Object.assign({}, agent);

    this.formChangesDetector.clear();
    this.bypassValidationSig.set(false);

    const formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);
          this.formDataSig.update(data => data);

          switch (prop) {
            case AgentKeys.p_agent_id:
              const mgaLeadAgentId = this.mgaLeadAgentIdSig();
              if (value === mgaLeadAgentId) {
                formData[AgentKeys.manager_id] = null;
              }
              this.bypassValidationSig.set(false);
              break;
            case AgentKeys.agent_type:
              if (new Set([AGENT_TYPE.AGENCY_PRINCIPAL, AGENT_TYPE.MGA_PRINCIPAL]).has(value)) {
                formData[AgentKeys.is_manager] = true;
              }
              break;
            case AgentKeys.p_mga_id:
              formData[AgentKeys.p_agency_id] = null;
              formData[AgentKeys.manager_id] = null;
              break;
            case AgentKeys.is_credited:
              value && (formData[AgentKeys.is_manager] = true);
              break;
          }
        }

        return true;
      },
    });

    this.formDataSig.set(formData);
  };

  generateAgentId = (sourceType: ChangeSourceType): void => {
    this._isAgentIdGenerationInProgress$.next(true);
    this.portalDataService
      .getNextAgentId(sourceType)
      .then(id => {
        this.formDataSig()[AgentKeys.p_agent_id] = String(id);
      })
      .catch(error => {
        console.error('Error in Agent Admin.', error);
      })
      .finally(() => {
        this._isAgentIdGenerationInProgress$.next(false);
      });
  };
}
